import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Grid, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PercentIcon from "@mui/icons-material/Percent";

import typography from "../../cssUtils/typography";
import {
  ColorsConstants,
  borderColors,
  dashboardCardBgColor,
  grayFontColor,
} from "../../cssUtils/constants";
import { useAuth } from "../../hooks/AuthProvider";
import { MFD_DASHBOARD_ENDPOINT } from "../../config/urls";
import AppTable from "../../common/app-table";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtPortfolio = (n) => {
  if (!n && n !== 0) return "—";
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(1)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(1)} L`;
  return `₹${Number(n).toLocaleString("en-IN")}`;
};

const fmtDelta = (val, suffix = "") =>
  val != null ? `${val >= 0 ? "+" : ""}${val}${suffix}` : "No prior data";

// ─── Constants ────────────────────────────────────────────────────────────────

const INTEREST_COLORS = {
  "New SIP":                ColorsConstants.primaryPurple100,
  "One-Time Investment":    "#f59e0b",
  "Review My Investments":  "#22c55e",
  "Product Offerings":      "#8b5cf6",
  "Financial Calculators":  "#ef4444",
  "Meeting / Conversation": "#06b6d4",
  "New Investment":         ColorsConstants.primaryPurple100,
  "Goal Planning":          "#f59e0b",
  "New Product Offering":   "#22c55e",
};
const COLOR_CYCLE = [
  ColorsConstants.primaryPurple100, "#f59e0b", "#22c55e",
  "#8b5cf6", "#ef4444", "#06b6d4",
];

const statusColors = {
  Pending:          { bg: "#f3e8ff", color: "#6b21a8" },
  Completed:        { bg: "#dcfce7", color: "#166534" },
  Failed:           { bg: "#fee2e2", color: "#991b1b" },
  Open:             { bg: "#e0f2fe", color: "#0369a1" },
  "In Progress":    { bg: "#fef3c7", color: "#92400e" },
  Converted:        { bg: "#dcfce7", color: "#166534" },
  "Not Interested": { bg: "#f3f4f6", color: "#374151" },
};

const activityColumns = [
  { id: "time",   label: "Time",   minWidth: 90  },
  { id: "name",   label: "Client", minWidth: 130 },
  { id: "phone",  label: "Phone",  minWidth: 120 },
  { id: "action", label: "Action", minWidth: 140 },
  { id: "detail", label: "Detail", minWidth: 180 },
  {
    id: "status",
    label: "Status",
    minWidth: 100,
    render: (row) => {
      const s = statusColors[row.status] || { bg: "#f3f4f6", color: "#374151" };
      return (
        <Box
          sx={{
            display: "inline-flex",
            px: 1.5,
            py: 0.25,
            borderRadius: "20px",
            backgroundColor: s.bg,
            color: s.color,
            ...typography.bodySmall,
            fontWeight: 500,
          }}
        >
          {row.status}
        </Box>
      );
    },
  },
];

// ─── Shared floating tooltip ────────────────────────────────────────────────
// Lightweight, dependency-free tooltip positioned relative to a container ref.
// Keeps the chart implementation as plain SVG (no new chart library dependency)
// while making hover interactions feel like a real, dynamic chart.

const ChartTooltip = ({ tooltip }) => {
  if (!tooltip) return null;
  return (
    <Box
      sx={{
        position: "absolute",
        left: tooltip.x,
        top: tooltip.y,
        transform: "translate(-50%, -115%)",
        backgroundColor: "#1e293b",
        color: "#fff",
        borderRadius: "6px",
        px: 1.25,
        py: 0.75,
        pointerEvents: "none",
        zIndex: 10,
        whiteSpace: "nowrap",
        boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
        transition: "left 0.05s linear, top 0.05s linear",
      }}
    >
      <Typography sx={{ ...typography.bodySmall, color: "#fff", fontWeight: 600, lineHeight: 1.3 }}>
        {tooltip.title}
      </Typography>
      {tooltip.lines?.map((line) => (
        <Typography key={line} sx={{ ...typography.bodySmall, color: "#cbd5e1", lineHeight: 1.3 }}>
          {line}
        </Typography>
      ))}
    </Box>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatCard = ({ label, value, delta, up, Icon, color, onValueClick }) => (
  <Paper
    elevation={0}
    onClick={onValueClick}
    sx={{
      border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
      borderRadius: "8px",
      p: 1,
      height: "100%",
      position: "relative",
      overflow: "hidden",
      backgroundColor: ColorsConstants.primaryWhite,
      cursor: onValueClick ? "pointer" : "default",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0, left: 0, right: 0,
        height: "3px",
        backgroundColor: color,
      },
    }}
  >
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <Box>
        <Typography sx={{ ...typography.bodySmall, fontSize: "9px", color: grayFontColor, textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.25 }}>
          {label}
        </Typography>
        <Typography
          sx={{
            ...typography.statExtraSmall,
            fontSize: "17px",
            color: ColorsConstants.primaryBlue100,
            mb: 0,
          }}
        >
          {value}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {up ? (
            <TrendingUpIcon sx={{ fontSize: 12, color: "#008026" }} />
          ) : (
            <TrendingDownIcon sx={{ fontSize: 12, color: "#b50000" }} />
          )}
          <Typography sx={{ ...typography.bodySmall, fontSize: "9px", color: up ? "#008026" : "#b50000", fontWeight: 500 }}>
            {delta}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          width: 24, height: 24, borderRadius: "6px",
          backgroundColor: dashboardCardBgColor,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <Icon sx={{ color, fontSize: 14 }} />
      </Box>
    </Box>
  </Paper>
);

const LeadsBarChart = ({ data = [] }) => {
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [hoveredKey, setHoveredKey] = useState(null);

  const maxVal   = Math.max(50, ...data.map((d) => d.leads));
  // Larger geometry so the chart properly fills the card instead of
  // sitting as a small, static-looking graphic.
  const chartH   = 50;
  const baseline = 10 + chartH;
  const barW     = 22;
  const groupW   = 90;
  const offsetX  = 34;

  const showTooltip = (e, key, title, lines) => {
    const bounds = containerRef.current?.getBoundingClientRect();
    if (!bounds) return;
    setHoveredKey(key);
    setTooltip({
      x: e.clientX - bounds.left,
      y: e.clientY - bounds.top,
      title,
      lines,
    });
  };

  const moveTooltip = (e) => {
    const bounds = containerRef.current?.getBoundingClientRect();
    if (!bounds) return;
    setTooltip((t) => (t ? { ...t, x: e.clientX - bounds.left, y: e.clientY - bounds.top } : t));
  };

  const hideTooltip = () => {
    setHoveredKey(null);
    setTooltip(null);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5, flexWrap: "wrap", gap: 1 }}>
        <Typography sx={{ ...typography.bodyLarge, fontSize: "13px", fontWeight: 600, color: ColorsConstants.primaryBlue100 }}>
          Leads — Month over Month
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "2px", backgroundColor: ColorsConstants.primaryPurple100 }} />
            <Typography sx={{ ...typography.bodySmall, fontSize: "9px", color: grayFontColor }}>New leads</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "2px", backgroundColor: "#22c55e" }} />
            <Typography sx={{ ...typography.bodySmall, fontSize: "9px", color: grayFontColor }}>Registered Leads</Typography>
          </Box>
        </Box>
      </Box>
      {data.length === 0 ? (
        <Typography sx={{ ...typography.bodySmall, color: grayFontColor, py: 4, textAlign: "center" }}>
          No lead data available
        </Typography>
      ) : (
        <Box ref={containerRef} sx={{ position: "relative" }}>
          <svg
            viewBox={`0 0 ${offsetX + 10 + data.length * groupW + 10} ${baseline + 45}`}
            height="130"
            preserveAspectRatio="xMinYMid meet"
            style={{ width: "100%", display: "block" }}
          >
            {[Math.round(maxVal), Math.round(maxVal * 0.75), Math.round(maxVal * 0.5), Math.round(maxVal * 0.25)].map((v) => {
              const y = 10 + ((maxVal - v) / maxVal) * chartH;
              return (
                <g key={v}>
                  <line x1={offsetX} y1={y} x2={offsetX + 10 + data.length * groupW} y2={y} stroke="#f1f5f9" strokeWidth="1" />
                  <text x={offsetX - 4} y={y + 4} textAnchor="end" fontSize="9" fill="#94a3b8">{v}</text>
                </g>
              );
            })}
            <line x1={offsetX} y1={10}       x2={offsetX} y2={baseline} stroke="#e2e8f0" strokeWidth="1" />
            <line x1={offsetX} y1={baseline} x2={offsetX + 10 + data.length * groupW} y2={baseline} stroke="#e2e8f0" strokeWidth="1" />

            {data.map((d, i) => {
              const x       = offsetX + 10 + i * groupW;
              const leadH   = Math.max(2, (d.leads     / maxVal) * chartH);
              const convH   = Math.max(2, (d.converted / maxVal) * chartH);
              const isLast  = i === data.length - 1;
              const leadClr = isLast ? ColorsConstants.primaryBlue100 : ColorsConstants.primaryPurple100;
              const convClr = isLast ? "#16a34a" : "#22c55e";
              const lblClr  = isLast ? ColorsConstants.primaryBlue100 : "#94a3b8";
              const fw      = isLast ? "600" : "400";

              const leadKey = `${d.month}-${d.year}-leads`;
              const convKey = `${d.month}-${d.year}-converted`;

              return (
                <g key={`${d.month}-${d.year}`}>
                  <rect
                    x={x} y={baseline - leadH} width={barW} height={leadH} rx="3"
                    fill={leadClr}
                    opacity={hoveredKey && hoveredKey !== leadKey ? 0.55 : isLast ? 1 : 0.85}
                    style={{ cursor: "pointer", transition: "opacity 0.15s ease" }}
                    onMouseEnter={(e) => showTooltip(e, leadKey, `${d.month} ${d.year ?? ""}`.trim(), [`New leads: ${d.leads}`])}
                    onMouseMove={moveTooltip}
                    onMouseLeave={hideTooltip}
                  />
                  <text x={x + barW / 2} y={baseline - leadH - 4} textAnchor="middle" fontSize="9" fill={leadClr} fontWeight={fw}>{d.leads}</text>

                  <rect
                    x={x + barW + 3} y={baseline - convH} width={barW} height={convH} rx="3"
                    fill={convClr}
                    opacity={hoveredKey && hoveredKey !== convKey ? 0.55 : isLast ? 1 : 0.85}
                    style={{ cursor: "pointer", transition: "opacity 0.15s ease" }}
                    onMouseEnter={(e) => showTooltip(e, convKey, `${d.month} ${d.year ?? ""}`.trim(), [
                      `Registered: ${d.converted}`,
                      d.leads > 0 ? `Conversion: ${Math.round((d.converted / d.leads) * 100)}%` : "Conversion: —",
                    ])}
                    onMouseMove={moveTooltip}
                    onMouseLeave={hideTooltip}
                  />
                  <text x={x + barW + 3 + barW / 2} y={baseline - convH - 4} textAnchor="middle" fontSize="9" fill={convClr} fontWeight={fw}>{d.converted}</text>

                  <text x={x + barW} y={baseline + 14} textAnchor="middle" fontSize="9" fill={lblClr} fontWeight={fw}>{d.month}</text>

                  <rect x={x - 2} y={baseline + 20} width={barW * 2 + 7} height={14} rx="3" fill={isLast ? "#dcfce7" : "#f0fdf4"} />
                  <text x={x + barW} y={baseline + 30} textAnchor="middle" fontSize="8.5" fill="#16a34a" fontWeight={fw}>
                    {d.leads > 0 ? `${Math.round((d.converted / d.leads) * 100)}% conv.` : "—"}
                  </text>
                </g>
              );
            })}
          </svg>
          <ChartTooltip tooltip={tooltip} />
        </Box>
      )}
    </Box>
  );
};

const DonutChart = ({ split = [], totalClients = 0 }) => {
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [hoveredLabel, setHoveredLabel] = useState(null);

  const r    = 36;
  const cx   = 50;
  const cy   = 50;
  const circ = 2 * Math.PI * r;
  let offset = 0;

  const showTooltip = (e, seg) => {
    const bounds = containerRef.current?.getBoundingClientRect();
    if (!bounds) return;
    setHoveredLabel(seg.label);
    setTooltip({
      x: e.clientX - bounds.left,
      y: e.clientY - bounds.top,
      title: seg.label,
      lines: [`${seg.pct}% of clients`],
    });
  };

  const moveTooltip = (e) => {
    const bounds = containerRef.current?.getBoundingClientRect();
    if (!bounds) return;
    setTooltip((t) => (t ? { ...t, x: e.clientX - bounds.left, y: e.clientY - bounds.top } : t));
  };

  const hideTooltip = () => {
    setHoveredLabel(null);
    setTooltip(null);
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Typography sx={{ ...typography.bodyLarge, fontSize: "13px", fontWeight: 600, color: ColorsConstants.primaryBlue100, mb: 0.5 }}>
        New Investor Engagement
      </Typography>
      {split.length === 0 ? (
        <Typography sx={{ ...typography.bodySmall, color: grayFontColor, py: 2 }}>
          No interest data available
        </Typography>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
            flex: 1,
            justifyContent: "center",
          }}
        >
          <Box ref={containerRef} sx={{ position: "relative", flexShrink: 0 }}>
            <svg width="90" height="90" viewBox="0 0 100 100">
              <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e2e8f0" strokeWidth="16" />
              {split.map((seg) => {
                const dash = (seg.pct / 100) * circ;
                const isHovered = hoveredLabel === seg.label;
                const el = (
                  <circle
                    key={seg.label}
                    cx={cx} cy={cy} r={r}
                    fill="none"
                    stroke={seg.color}
                    strokeWidth={isHovered ? 19 : 16}
                    strokeDasharray={`${dash} ${circ}`}
                    strokeDashoffset={-offset}
                    transform={`rotate(-90 ${cx} ${cy})`}
                    opacity={hoveredLabel && !isHovered ? 0.5 : 1}
                    style={{ cursor: "pointer", transition: "stroke-width 0.15s ease, opacity 0.15s ease" }}
                    onMouseEnter={(e) => showTooltip(e, seg)}
                    onMouseMove={moveTooltip}
                    onMouseLeave={hideTooltip}
                  />
                );
                offset += dash;
                return el;
              })}
              <text x={cx} y={cy + 5} textAnchor="middle" fontSize="13" fontWeight="600" fill={ColorsConstants.primaryBlue100}>
                {totalClients}
              </text>
            </svg>
            <ChartTooltip tooltip={tooltip} />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {split.map((s) => (
              <Box
                key={s.label}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  cursor: "pointer",
                  opacity: hoveredLabel && hoveredLabel !== s.label ? 0.5 : 1,
                  transition: "opacity 0.15s ease",
                }}
                onMouseEnter={() => setHoveredLabel(s.label)}
                onMouseLeave={() => setHoveredLabel(null)}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: "2px", backgroundColor: s.color, flexShrink: 0 }} />
                <Typography sx={{ ...typography.bodySmall, fontSize: "9px", color: grayFontColor }}>{s.label} ({s.pct}%)</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const Dashboard = ({ onNavigate }) => {
  const { getValidToken } = useAuth();
  const [dashData, setDashData] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const token  = await getValidToken();
      const tenant = localStorage.getItem("tenant");
      const res = await fetch(MFD_DASHBOARD_ENDPOINT, {
        headers: { Authorization: `Bearer ${token}`, "X-Tenant-Id": tenant },
      });
      if (!res.ok) throw new Error("Failed to fetch dashboard");
      setDashData(await res.json());
    } catch (err) {
      console.error("Error fetching dashboard:", err);
    } finally {
      setLoading(false);
    }
  }, [getValidToken]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const s            = dashData?.stats;
  const pendingCount = s?.pending_report_requests ?? 0;
  const today        = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

  const goTo = (id) => onNavigate?.(id);

  const statCards = s
    ? [
        {
          label: "Total Clients",
          value: String(s.total_clients ?? 0),
          delta: `+${s.new_clients_this_month ?? 0} this month`,
          up: (s.new_clients_this_month ?? 0) >= 0,
          Icon: PeopleAltIcon,
          color: ColorsConstants.primaryPurple100,
          onValueClick: () => goTo("mutual_fund_distributor.clients"),
        },
        {
          label: "AUM",
          value: fmtPortfolio(s.total_portfolio_value),
          delta: fmtDelta(s.portfolio_value_mom_pct, "% MoM"),
          up: (s.portfolio_value_mom_pct ?? 0) >= 0,
          Icon: AccountBalanceIcon,
          color: "#008026",
        },
        {
          label: "New Leads",
          value: String(s.new_leads_total ?? 0),
          delta: `+${s.new_leads_today ?? 0} new today`,
          up: (s.new_leads_today ?? 0) >= 0,
          Icon: LeaderboardIcon,
          color: ColorsConstants.primaryPurple100,
          onValueClick: () => goTo("mutual_fund_distributor.leads"),
        },
        {
          label: "Pending Customer Requests",
          value: String(s.pending_report_requests ?? 0),
          delta: `${s.pending_report_requests ?? 0} reports pending`,
          up: false,
          Icon: AssignmentIcon,
          color: "#b50000",
          onValueClick: () => goTo("mutual_fund_distributor.report_requests"),
        },
        {
          label: "Avg. AUM XIRR",
          value: s.avg_xirr != null ? `${s.avg_xirr}%` : "—",
          delta: fmtDelta(s.avg_xirr_mom_delta, "% vs last month"),
          up: (s.avg_xirr_mom_delta ?? 0) >= 0,
          Icon: PercentIcon,
          color: "#008026",
        },
      ]
    : [];

  const interestSplit = (dashData?.interest_split ?? []).map((seg, i) => ({
    label: seg.interest,
    pct:   seg.pct,
    color: INTEREST_COLORS[seg.interest] ?? COLOR_CYCLE[i % COLOR_CYCLE.length],
  }));

  // Responsive table height: shows significantly more rows on desktop/laptop
  // while staying safe on tablet viewports. Fully controlled from here —
  // AppTable's existing `maxHeight` prop already supports this.
  const tableMaxHeight = isTablet ? 460 : 560;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1.5 }}>

      {/* Stat cards */}
      <Grid container spacing={1} sx={{ mb: 1 }}>
        {statCards.map((card) => (
          <Grid item xs={12} sm={6} md={2.4} key={card.label}>
            <StatCard {...card} />
          </Grid>
        ))}
      </Grid>

      {/* Charts row */}
      <Grid container spacing={1} sx={{ mb: 1 }} alignItems="stretch">
        <Grid item xs={12} md={7}>
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
              borderRadius: "8px",
              p: { xs: 1, md: 1.5 },
              height: "100%",
              minHeight: 160,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <LeadsBarChart data={dashData?.monthly_leads ?? []} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
              borderRadius: "8px",
              p: { xs: 1, md: 1.5 },
              height: "100%",
              minHeight: 160,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <DonutChart split={interestSplit} totalClients={s?.total_clients ?? 0} />
          </Paper>
        </Grid>
      </Grid>

      {/* Recent activity table */}
      <Paper
        elevation={0}
        sx={{ border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`, borderRadius: "8px", overflow: "hidden" }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
          }}
        >
          <Typography sx={{ ...typography.h2, color: ColorsConstants.primaryBlue100 }}>
            Recent bot activity
          </Typography>
          <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
            Today, {today}
          </Typography>
        </Box>
        <AppTable
          columns={activityColumns}
          rows={dashData?.activity ?? []}
          rowKey="id"
          maxHeight={tableMaxHeight}
          hideMainScroll={false}
          emptyMessage="No recent activity"
          paperSx={{ border: "none", borderRadius: 0, mb: 0 }}
        />
      </Paper>

    </Box>
  );
};

export default Dashboard;